"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import { supabase } from "../supabaseClient";
import type { Session } from "@supabase/supabase-js";

interface PrivateRouteProps {
  children: React.ReactNode;
}

export default function PrivateRoute({ children }: PrivateRouteProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Initial session fetch
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    // Keep UI in sync with auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
      setLoading(false);
    });

    // Handle background tab throttling:
    // Pause refresh when hidden; resume + re-check session when visible.
    const handleVisibility = async () => {
      if (document.visibilityState === "visible") {
        // Resume token auto-refresh and force a session check
        (supabase.auth as any).startAutoRefresh?.();
        try {
          const { data } = await supabase.auth.getSession();
          if (!mounted) return;
          setSession(data.session);
          setLoading(false);
        } catch {
          /* no-op */
        }
      } else {
        (supabase.auth as any).stopAutoRefresh?.();
      }
    };

    // Extra safety: on window focus, re-check the session
    const handleFocus = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        setLoading(false);
      } catch {
        /* no-op */
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    // Run once to align with current visibility state
    handleVisibility();

    // Stuck spinner guard: if still loading after a short delay, kick the session once
    const guardId = window.setTimeout(async () => {
      if (!mounted) return;
      if (loading) {
        try {
          const { data } = await supabase.auth.getSession();
          if (!mounted) return;
          setSession(data.session);
        } finally {
          if (mounted) setLoading(false);
        }
      }
    }, 2500);

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
      clearTimeout(guardId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  return session ? <>{children}</> : <Navigate to="/login" replace />;
}
